import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ImageCarousel, 
  ImageGrid, 
  ProfessionalImage, 
  professionalImages 
} from "./ImageCarousel.js";

// Cybersecurity Services Page
export const CybersecurityService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Cybersecurity Services</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Security Solutions for the Private Commercial or Government Entire Enterprise
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          {/* Professional Cybersecurity Image */}
          <div className="mb-16">
            <ProfessionalImage 
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjeWJlcnNlY3VyaXR5fGVufDB8fHx8MTc1NTY2MjkwOHww&ixlib=rb-4.1.0&q=85"
              alt="Advanced cybersecurity technology with circuit board"
              caption="Advanced cybersecurity technology solutions"
              className="max-w-4xl mx-auto"
              overlay={true}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Comprehensive Cyber Protection</h2>
              <p className="text-lg text-gray-600 mb-6">
                Riley Technology Solutions (RTS) provides customers with the most comprehensive tools to secure their network and mission-critical data. We're an authorized Fortinet business partner.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Threats to networks and the data within are persistently evolving, which means that having the right combination of defenses is critical to safeguarding today's enterprises.
              </p>
              
              <div className="bg-orange-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-800 mb-2">Authorized Partner</h3>
                <p className="text-orange-700">Fortinet Business Partner</p>
                <p className="text-sm text-orange-600 mt-2">Leading cybersecurity manufacturer</p>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Our Cybersecurity Offerings</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Prevention/Protection</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Next-generation firewalls</li>
                      <li>• Intrusion detection/protection systems</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">End-User Protection</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Data encryption and antivirus</li>
                      <li>• Endpoint, cloud, mobile, and virtual security</li>
                      <li>• Application security</li>
                      <li>• Internet content security/Web-filtering</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Vulnerability Testing</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Compliance and penetration testing</li>
                      <li>• Vulnerability detection and patch management</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Visibility and Management</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Digital forensics</li>
                      <li>• Deep packet inspection (DPI)</li>
                      <li>• Security incident response management</li>
                      <li>• Disaster recovery & business continuity</li>
                      <li>• Centralized security & network monitoring</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Secure Your Enterprise Today</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact our Cybersecurity Sales Team to learn more about our range of cybersecurity services
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
          >
            Contact Cybersecurity Team
          </button>
        </div>
      </section>
    </div>
  );
};

// Cloud Innovation Service Page
export const CloudService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Managed Cloud Innovation and Strategy</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Riley Technology Solutions (RTS) will help your company optimize and engage using the Cloud to exceed maximizing innovation in your organizations
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          {/* Professional Cloud Technology Image */}
          <div className="mb-16">
            <ProfessionalImage 
              src="https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg"
              alt="Modern data center with cloud infrastructure"
              caption="State-of-the-art cloud infrastructure and data centers"
              className="max-w-4xl mx-auto"
              overlay={true}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Transform Your Operations</h2>
              <p className="text-lg text-gray-600 mb-6">
                You can take your company to new levels by leveraging cloud technologies. Working with RTS to achieve your organization's cloud platform potential will make a significant difference to your financial bottom line with short to long term competitive benefits.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                The cloud can enable your company to be more efficient and productive. Partner with RTS to simplify your cloud transformation and spend more time creating reliable and secure technology experiences.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">🔄</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Transform Operations</h4>
                    <p className="text-gray-600">Enable efficiency and productivity improvements</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">🚀</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Modernize Solutions</h4>
                    <p className="text-gray-600">Build applications that are easier to maintain and develop</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">☁️</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Cloud Adoption</h4>
                    <p className="text-gray-600">Focus on creating high-performing cloud-based business assets</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Cloud Technologies & Platforms</h3>
                
                <div className="mb-6">
                  <h4 className="text-xl font-semibold text-blue-800 mb-4">Microsoft Azure</h4>
                  <p className="text-gray-600 mb-4">Specialized Azure implementation and optimization services</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Automate and Innovate</h4>
                    <p className="text-sm text-gray-600">Show value early and often, leverage AI and machine learning</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Industry Expertise</h4>
                    <p className="text-sm text-gray-600">30+ years of experience delivering complex solutions across all industries</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Strategic Approach</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Involve key stakeholders</li>
                      <li>• Calculate total costs of ownership</li>
                      <li>• Discover and evaluate apps</li>
                      <li>• Select migration approach</li>
                      <li>• Apply approved migration plan</li>
                      <li>• Establish 24/7 monitoring dashboard</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready for Cloud Transformation?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us help you identify and prioritize business processes that can be automated and deliver more resilient solutions
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
          >
            Start Cloud Journey
          </button>
        </div>
      </section>
    </div>
  );
};

// Network Cabling Service Page
export const CablingService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Managed Network Cabling Services</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Network cabling is at the core of RTS capabilities. We've helped install miles of fiber optic cables in our 20+ years of business
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          {/* Professional IT Infrastructure Image */}
          <div className="mb-16">
            <ProfessionalImage 
              src="https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxJVCUyMGluZnJhc3RydWN0dXJlfGVufDB8fHx8MTc1NTY2Mjg5OXww&ixlib=rb-4.1.0&q=85"
              alt="Professional IT infrastructure and network hardware"
              caption="Advanced IT infrastructure and hardware expertise"
              className="max-w-4xl mx-auto"
              overlay={true}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Structured Cabling Excellence</h2>
              <p className="text-lg text-gray-600 mb-6">
                From network cabling, structured cabling, rack maintenance, or any other network cable-related services, we have dedicated network architecture professionals that ensure your IT environment is optimized for success.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Physical infrastructure is still a crucial component of IT because it is so interconnected with other areas of your network. Build and expand upon the foundation of your IT infrastructure with our network cabling services.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">BICSI Certified</h3>
                <p className="text-blue-700">Our staff includes BICSI certified technicians</p>
                <p className="text-sm text-blue-600 mt-2">Projects designed to meet latest BICSI standards</p>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Network Cabling Services</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Installation Services</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Installing new data cables</li>
                      <li>• Building efficient data centers</li>
                      <li>• Fiber optic installation</li>
                      <li>• Network upgrades with reliable Wi-Fi</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Professional Services</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Patch panel configuration</li>
                      <li>• Data closet cleanup</li>
                      <li>• Professional cable management</li>
                      <li>• Fiber termination</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Security & Access</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Network security system setup</li>
                      <li>• Access control integration</li>
                      <li>• Surveillance system connectivity</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Ongoing Support</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 30+ years of installation experience</li>
                      <li>• Dedicated support channels</li>
                      <li>• Project lifecycle protection</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Installation & Deinstallation</h2>
            <p className="text-xl text-gray-600">Structured cabling is the backbone to your business operations</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">New Construction & Expansion</h3>
              <p className="text-lg text-gray-600 mb-6">
                Whether you're working on a new construction or expansion project, we'll work with your contractors to install the telephone and data cables you need.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">✓</span>
                  <span className="text-gray-700">Work with contractors on new construction</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">✓</span>
                  <span className="text-gray-700">Telephone and data cable installation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">✓</span>
                  <span className="text-gray-700">RCDD (Registered Communications Distribution Designer) contracts</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Network Functionality</h3>
              <p className="text-lg text-gray-600 mb-6">
                There's no reason to compromise when it comes to your computer network. We provide government and commercial networking services with increased functionality.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">✓</span>
                  <span className="text-gray-700">Fiber optic installations and repairs</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">✓</span>
                  <span className="text-gray-700">Network updates with increased functionality</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">✓</span>
                  <span className="text-gray-700">Secure collaboration networks</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Schedule Cabling Services</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us for small business cabling and commercial networking services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
            >
              Schedule Service
            </button>
            <a 
              href="tel:813.720.9057"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-800 transition duration-300"
            >
              Call 813.720.9057
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Surveillance Service Page
export const SurveillanceService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">IP Surveillance</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Eyes for Your Commercial or Government Properties - Protect and secure your building properties internally or externally
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          {/* Professional Surveillance Technology Image */}
          <div className="mb-16">
            <ImageGrid 
              images={[
                {
                  url: "https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxJVCUyMGluZnJhc3RydWN0dXJlfGVufDB8fHx8MTc1NTY2Mjg5OXww&ixlib=rb-4.1.0&q=85",
                  alt: "Advanced IT infrastructure for surveillance systems",
                  title: "IT Infrastructure",
                  description: "Professional surveillance system integration"
                },
                {
                  url: "https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg",
                  alt: "Data center supporting surveillance operations",
                  title: "Data Management",
                  description: "Secure data storage and monitoring"
                }
              ]}
              columns={2}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Keep Watch Over Your Properties</h2>
              <p className="text-lg text-gray-600 mb-6">
                Tired of being in the dark about what's happening around your commercial properties? Now you can keep watch over your commercial or government agency place of business with a reliable IP surveillance system.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                RTS is a security camera managed installer so we can outfit your building with any number of IT powerful cameras. You can rely on our experienced technicians to design an efficient system that will give you as much coverage as you want.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">📹</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Professional Installation</h4>
                    <p className="text-gray-600">Experienced technicians design efficient coverage systems</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">🌐</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Network Integration</h4>
                    <p className="text-gray-600">Connect to your IT network for remote monitoring</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">🔧</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Full Service Support</h4>
                    <p className="text-gray-600">Installation, configuration, and ongoing maintenance</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Complete Installation Process</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">We Handle Everything:</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li>• Install the wiring and connect each camera properly</li>
                      <li>• Configure your security system to suit your needs</li>
                      <li>• Troubleshoot potential problems</li>
                      <li>• Perform necessary maintenance</li>
                      <li>• Coordinate with you every step of the process</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">System Features:</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li>• IT powerful cameras with full coverage</li>
                      <li>• Remote monitoring capabilities</li>
                      <li>• Integration with existing IT networks</li>
                      <li>• Custom configuration for business needs</li>
                      <li>• Professional technical support</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">You Won't Lift a Finger</h4>
                    <p className="text-sm text-blue-700">
                      We coordinate every step of the installation process to ensure you end up with an IP surveillance system that covers all bases and meets your business requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Get Affordable & Effective Surveillance</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us for an affordable and effective commercial or government IP surveillance system
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
            >
              Get Quote
            </button>
            <a 
              href="tel:813.720.9057"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-800 transition duration-300"
            >
              Call 813.720.9057
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Access Control Service Page
export const AccessControlService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Building Access Control</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Control Your Commercial or Government Building Access - Business solutions that protect your employees, inventory, equipment, and more
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Secure Access Control</h2>
              <p className="text-lg text-gray-600 mb-6">
                Locking your doors is the first step to securing your office. But having to open those doors for your workers day after day, and hour after hour, is an unwelcome distraction.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                An access control system installation solves this problem by giving you the power to grant access to specific people on site, or on the road.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">🔐</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Digital Access Control</h4>
                    <p className="text-gray-600">Digital keypad or card-based systems</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">📹</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Camera Integration</h4>
                    <p className="text-gray-600">Cameras activate when doors open or close</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">👥</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Employee Management</h4>
                    <p className="text-gray-600">Easy access control for who comes and goes</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Access Control Benefits</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Security Advantages:</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li>• Remove employee access without changing locks</li>
                      <li>• Prevent non-IT employees from accessing equipment</li>
                      <li>• Lock sensitive documents in restricted storage rooms</li>
                      <li>• Track and monitor all building access</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">System Features:</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li>• Digital keypad systems</li>
                      <li>• Card-based access control</li>
                      <li>• Integration with surveillance cameras</li>
                      <li>• Remote access management</li>
                      <li>• Detailed access logs and reporting</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Professional Installation:</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li>• Complete system design and setup</li>
                      <li>• Integration with existing security systems</li>
                      <li>• Employee training and support</li>
                      <li>• Ongoing maintenance and updates</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Perfect For:</h4>
                    <p className="text-sm text-green-700">
                      Commercial offices, government buildings, healthcare facilities, educational institutions, and any business requiring secure access control.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Set Up Access Control Today</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Find out more about access control system installations and camera integrations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
            >
              Get Started
            </button>
            <a 
              href="tel:813.720.9057"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-800 transition duration-300"
            >
              Call 813.720.9057
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Commercial Solutions - Small Business Page
export const SmallBusinessPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Commercial Solutions for Small Businesses</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Time and resource saving products, services, and support for reducing small business technology costs!
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Small Business Technology Challenges</h2>
              <p className="text-lg text-gray-600 mb-6">
                Small Businesses face a daily challenge when it comes to the implementation of new technology in an oversaturated market of tools promising to assist in more effective management of systems and data.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Many small business owners find the cost to time and resources required in keeping up with constantly scaling systems a headache. So how can small businesses do more with less resources while avoiding costly downtime?
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">RTS Solution</h3>
                <p className="text-blue-700">Solutions tailored to your business requirements!</p>
                <p className="text-sm text-blue-600 mt-2">Cost-effective technology management for small businesses</p>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Small Business Solutions</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Cost-Effective Services:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Affordable IT support packages</li>
                      <li>• Scalable technology solutions</li>
                      <li>• Preventive maintenance programs</li>
                      <li>• Budget-friendly hardware options</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Time-Saving Tools:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Automated backup solutions</li>
                      <li>• Cloud-based productivity tools</li>
                      <li>• Remote monitoring and support</li>
                      <li>• Streamlined communication systems</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Resource Optimization:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Efficient system management</li>
                      <li>• Reduced downtime strategies</li>
                      <li>• Technology consulting services</li>
                      <li>• Growth-oriented IT planning</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Do More with Less Resources</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let RTS help your small business avoid costly downtime while maximizing efficiency
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
          >
            Get Small Business Solutions
          </button>
        </div>
      </section>
    </div>
  );
};

// Commercial Solutions - Mid-Market Page
export const MidMarketPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Commercial Solutions for Mid-Market Businesses</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Top of the line and cost-efficient solutions and services for assisting Mid-Market Businesses in maintaining growth!
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Mid-Market Technology Challenges</h2>
              <p className="text-lg text-gray-600 mb-6">
                Mid-Market Businesses face a unique set of technology challenges when compared with small businesses and enterprises that if left unaddressed could stunt their continued growth and cripple operations.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Mid-market business executives fear costly downtime due to data expansion, compliance regulations, or lack of resources to accomplish mission critical updates. So how do mid-market business executives keep pace with rapid expansion of data and requirements without potential downtime?
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">📊</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Data Expansion Management</h4>
                    <p className="text-gray-600">Handle rapid growth in data requirements</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">📋</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Compliance Solutions</h4>
                    <p className="text-gray-600">Meet regulatory requirements efficiently</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">⚡</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Mission Critical Updates</h4>
                    <p className="text-gray-600">Ensure updates without operational disruption</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Mid-Market Solutions</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Growth Support:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Scalable infrastructure solutions</li>
                      <li>• Data management strategies</li>
                      <li>• Performance optimization</li>
                      <li>• Capacity planning services</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Compliance & Security:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Regulatory compliance management</li>
                      <li>• Security framework implementation</li>
                      <li>• Risk assessment and mitigation</li>
                      <li>• Audit preparation and support</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Operational Excellence:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Zero-downtime update strategies</li>
                      <li>• Business continuity planning</li>
                      <li>• Disaster recovery solutions</li>
                      <li>• 24/7 monitoring and support</li>
                    </ul>
                  </div>
                  
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-orange-800 mb-2">RTS Advantage</h4>
                    <p className="text-sm text-orange-700">
                      Solutions tailored specifically to mid-market business requirements and growth patterns.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Maintain Growth Without Downtime</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            RTS is here to help mid-market clients with solutions tailored to their business requirements
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
          >
            Get Mid-Market Solutions
          </button>
        </div>
      </section>
    </div>
  );
};

// This continues with more components but I need to split into another file due to length